// A project's ACC or Procore settings: where each kind of file is delivered (shop
// drawings, drawings, the Revit model, the Navisworks model) and whether approved shop
// drawings go there automatically. For Procore it also links the BidLight project to a
// Procore project (the card's Procore logo opens this window on that step).
//
// SEVERAL DESTINATIONS PER PROVIDER (owner 2026-10-24: "connect many folders from many
// accounts per acc and per procore"): the strip at the top picks one - "Procore 1",
// "Procore 2 - Client" - and everything below edits that one, with its own account,
// folders and automatic deliveries. "+ Add destination" starts another.
import React, { useCallback, useEffect, useState } from "react";
import { Input, Modal, Select, Spin, Switch } from "antd";
import addNotification from "helpers/notify";
import { connectAcc } from "components/Acc/AccConnectionPanel";
import FolderPicker from "./FolderPicker";
import { AutodeskLogo, ProcoreLogo } from "./ProviderLogos";
import { getProjectIntegrations, saveProjectIntegration, removeProjectIntegration } from "./integrationsApi";
import { refreshIntegrationCards, useProviderStatus } from "./useIntegrations";
import { connectProcore, PROCORE_METHOD } from "./ProcoreConnectionPanel";
import { openProcoreConnect } from "./ProcoreConnectModal";
import { openFolderPaths } from "./FolderPathsPanel";

export const PROVIDER_NAME = { acc: "ACC", procore: "Procore" };

const KIND_ROWS = [
  { kind: "shopDrawings", label: "Shop drawings", hint: "Approved shop drawings, when Deliver to is ticked (here or on the Shop Drawings page). The folder shown is the shop drawings UPLOAD folder in use." },
  { kind: "drawings", label: "Drawings", hint: "Where sheets and drawing sets will be delivered." },
  { kind: "models", label: "Revit model (.rvt)", hint: "The models UPLOAD folder in use for .rvt." },
  { kind: "navisworks", label: "Navisworks (.nwc / .nwd)", hint: "The models UPLOAD folder in use for .nwd." },
];

// THE FOUR FOLDER SLOTS (owner 2026-10-25): what this destination uses for each, the
// project's own or the company default; set in the Procore / ACC folders panel
const SLOT_ROWS = [
  { slot: "modelsDownload", label: "Models - download" },
  { slot: "modelsUpload", label: "Models - upload" },
  { slot: "shopDrawingsDownload", label: "Shop drawings - download" },
  { slot: "shopDrawingsUpload", label: "Shop drawings - upload" },
];

// WHO UPLOADS AND DOWNLOADS FOR THIS PROJECT (owner 2026-10-21): remembered per project
// and provider on the server; set by the first save, moved only by "Use my account".
function ActorRow({ actor, name, canEdit, saving, onUseMine }) {
  const who = actor
    ? `${actor.userName || actor.userEmail || "a BidLight user"}${actor.providerEmail ? ` (${name}: ${actor.providerEmail})` : ""}`
    : "";
  return (
    <div className="integration-section">
      <div className="integration-section-title">Uploads and downloads run as</div>
      <div className="integration-row">
        {actor ? (
          <span>
            {who}
            {actor.isMe ? " - you" : ""}
            {!actor.connected ? <span className="text-danger"> - this {name} connection is not working; nothing can be sent until it is reconnected or another account takes over</span> : null}
          </span>
        ) : (
          <span className="text-muted">Nobody yet - the first folder you save makes it your {name} account</span>
        )}
        {canEdit && actor && !actor.isMe ? (
          <button className="integration-link-btn" disabled={saving} onClick={onUseMine}>
            Use my account
          </button>
        ) : null}
      </div>
      {actor && actor.providerUserId ? <small className="text-muted">{name} user id {actor.providerUserId}</small> : null}
    </div>
  );
}

// THE ACCOUNT A DESTINATION USES: the company's Procore / Autodesk accounts (both the
// company's since 2026-10-25).
function AccountRow({ provider, name, data, connections, canEdit, saving, onPick }) {
  const mine = connections || [];
  const primary = mine.find((c) => c.primary);
  const value = data.connection || (primary ? primary.id : undefined);
  const addAnother = () => {
    if (provider === "procore") openProcoreConnect(null, { connection: "new" });
    else connectAcc({ connection: "new" });
  };
  return (
    <div className="integration-section">
      <div className="integration-section-title">{name} account for this destination</div>
      <div className="integration-row">
        <Select
          size="small"
          style={{ minWidth: 280 }}
          value={value}
          disabled={!canEdit || saving || !mine.length}
          placeholder={`No ${name} account connected`}
          onChange={(id) => onPick(primary && id === primary.id ? "" : id)}
          options={mine.map((c) => ({
            value: c.id,
            label: `${c.label}${c.primary ? " (company account)" : ""}${c.connected ? "" : " - not connected"}`,
          }))}
          data-testid="dest-account"
        />
        {canEdit ? (
          <button className="integration-link-btn" onClick={addAnother} data-testid="dest-connect-another">
            Connect another {name} account
          </button>
        ) : null}
      </div>
      <small className="text-muted">
        {`Every ${provider === "acc" ? "Autodesk" : "Procore"} account your company has connected. Uploads and downloads to this destination use the one chosen here.`}
      </small>
    </div>
  );
}

export default function IntegrationSettingsModal({ provider, projectId, projectTitle, canEdit, startOnLink = false, onClose }) {
  const [all, setAll] = useState(null);
  const [selected, setSelected] = useState(""); // destination id | "new" | "" (the first, not saved yet)
  const [error, setError] = useState("");
  const [picking, setPicking] = useState(null); // kind | "link"
  const [saving, setSaving] = useState(false);
  const [label, setLabel] = useState("");
  const name = PROVIDER_NAME[provider];
  const procoreStatus = useProviderStatus("procore");

  // this provider's destinations, oldest first, and the one being edited - merged with
  // the provider-wide facts (configured, connected, the linked Revit file)
  const mineDests = ((all && all.destinations) || []).filter((d) => d.provider === provider);
  const base = all ? all[provider] : null;
  const current = selected === "new" ? null : mineDests.find((d) => d.destination === selected) || (selected ? null : mineDests[0]) || null;
  const blank = { targets: {}, autoDeliver: {}, actor: null, linked: false, accountId: "", containerId: "", linkPath: "", label: "", connection: "" };
  const data = base ? { ...base, ...blank, ...(current || {}), configured: base.configured, connected: base.connected, canUpload: base.canUpload, modelLink: base.modelLink, oauthConfigured: base.oauthConfigured } : null;
  const destId = current ? current.destination : selected === "new" ? "new" : "";
  if (data && provider === "procore") data.linked = !!data.containerId;

  const load = useCallback(async () => {
    try {
      const d = await getProjectIntegrations(projectId);
      setAll(d);
      setError("");
      return d[provider];
    } catch (e) {
      setError(e.message);
      return null;
    }
  }, [projectId, provider]);

  useEffect(() => {
    setLabel((current && current.label) || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destId]);

  useEffect(() => {
    load().then((d) => {
      if (startOnLink && d && d.connected && provider === "procore" && !d.linked && canEdit) setPicking("link");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  const save = async (body, done) => {
    setSaving(true);
    try {
      const v = await saveProjectIntegration(projectId, provider, body, destId || undefined);
      await load();
      // a new destination: keep editing it
      if (v && v.destination) setSelected(v.destination);
      refreshIntegrationCards();
      if (done) addNotification(done, "success");
      setPicking(null);
    } catch (e) {
      addNotification(e.message, "danger");
    } finally {
      setSaving(false);
    }
  };

  const unlinkProcore = () =>
    Modal.confirm({
      title: `Unlink "${projectTitle}" from Procore?`,
      content: "Its Procore folders are forgotten and nothing more is delivered there. Files already in Procore stay.",
      okText: "Unlink",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await removeProjectIntegration(projectId, "procore", current ? current.destination : undefined);
          refreshIntegrationCards();
          setSelected("");
          await load();
        } catch (e) {
          addNotification(e.message, "danger");
        }
      },
    });

  // where a folder picker opens: inside the linked Procore project, or the ACC project
  // that holds the linked Revit file
  const start = () => {
    if (!data) return null;
    if (provider === "procore" && data.linked) {
      const [accountName, containerName] = String(data.linkPath || "").split(" / ");
      return { accountId: data.accountId, containerId: data.containerId, accountName, containerName };
    }
    if (provider === "acc" && data.modelLink) {
      const [accountName, containerName] = String(data.modelLink.path || "").split(" / ");
      return { accountId: data.modelLink.hubId, containerId: data.modelLink.accProjectId, accountName, containerName };
    }
    return null;
  };

  const removeDestination = () =>
    Modal.confirm({
      title: `Remove ${current ? current.name : name} from "${projectTitle}"?`,
      content: "Its folders are forgotten and nothing more is sent there or read from there. Files already there stay.",
      okText: "Remove",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await removeProjectIntegration(projectId, provider, current.destination);
          refreshIntegrationCards();
          setSelected("");
          await load();
        } catch (e) {
          addNotification(e.message, "danger");
        }
      },
    });

  // the picker browses as the destination's account when it is one this person can use
  const myConns = (all && all.connections && all.connections[provider]) || [];
  const browseAs = data && data.connection && myConns.some((c) => c.id === data.connection) ? data.connection : "";

  const strip =
    data && data.connected ? (
      <div className="integration-dest-strip" data-testid="dest-strip">
        {(mineDests.length ? mineDests : [{ destination: "", name }]).map((d) => (
          <button
            key={d.destination || "first"}
            className={`integration-dest-tab${(current ? current.destination : "") === d.destination && selected !== "new" ? " active" : ""}`}
            onClick={() => { setPicking(null); setSelected(d.destination); }}
          >
            {d.name || name}
          </button>
        ))}
        {selected === "new" ? <button className="integration-dest-tab active">New {name} destination</button> : null}
        {canEdit && mineDests.length ? (
          <button className="integration-link-btn" onClick={() => { setPicking(null); setSelected("new"); }} data-testid="dest-add">
            + Add destination
          </button>
        ) : null}
      </div>
    ) : null;

  const Logo = provider === "acc" ? AutodeskLogo : ProcoreLogo;
  let content;
  if (error) content = <p className="text-danger">{error}</p>;
  else if (!data) content = <Spin />;
  else if (!data.configured) content = <p>{name} is not set up on this server yet.</p>;
  else if (!data.connected) {
    content = (
      <div>
        <p>Connect your {name} account first, then choose where this project&apos;s files are delivered.</p>
        <button className="header-btn text-white bg-bid border-bid px-3" onClick={provider === "acc" ? connectAcc : connectProcore}>
          Connect {name}
        </button>
      </div>
    );
  } else if (picking) {
    const kindRow = KIND_ROWS.find((k) => k.kind === picking);
    content = (
      <div>
        <p className="mb-2">
          <b>{picking === "link" ? "Choose the Procore project for this BidLight project" : `${kindRow.label}: open the folder to deliver into`}</b>
        </p>
        <FolderPicker
          provider={provider}
          connection={browseAs}
          mode={picking === "link" ? "project" : "folder"}
          start={picking === "link" ? null : start()}
          busy={saving}
          onCancel={() => setPicking(null)}
          onPick={(t) =>
            picking === "link"
              ? save({ accountId: t.accountId, containerId: t.containerId, linkPath: t.path }, `Linked to Procore: ${t.path}`)
              : save({ targets: { [picking]: t } }, `${kindRow.label} will be delivered to ${t.path}`)
          }
        />
      </div>
    );
  } else {
    content = (
      <div className="integration-settings">
        <div className="integration-section">
          <div className="integration-section-title">Destination name</div>
          <div className="integration-row">
            <Input
              size="small"
              style={{ maxWidth: 280 }}
              value={label}
              placeholder={selected === "new" ? "e.g. Client hub" : "Optional - e.g. Our Procore"}
              disabled={!canEdit || saving}
              onChange={(e) => setLabel(e.target.value)}
              onBlur={() => { if (label !== ((current && current.label) || "") && (current || label)) save({ label }); }}
              data-testid="dest-label"
            />
            {canEdit && current && mineDests.length > 1 ? (
              <button className="integration-link-btn danger" onClick={removeDestination} data-testid="dest-remove">
                Remove this destination
              </button>
            ) : null}
          </div>
        </div>
        <div className="integration-section" data-testid="dest-folder-paths">
          <div className="integration-section-title">Procore / ACC folders</div>
          {SLOT_ROWS.map(({ slot, label }) => {
            const p = (data.folderPaths && data.folderPaths[slot]) || "";
            return (
              <div key={slot} className="integration-row">
                <b style={{ minWidth: 180 }}>{label}:</b>
                <span className={p ? "integration-path" : "text-muted"}>{p || "Not set"}</span>
              </div>
            );
          })}
          <button className="integration-link-btn" style={{ marginLeft: 0 }} onClick={() => openFolderPaths(projectId, projectTitle)} data-testid="dest-open-folder-paths">
            Set folders - models (.rvt / .nwc / .nwd) and shop drawings, download and upload, per account
          </button>
        </div>
        <AccountRow
          provider={provider}
          name={name}
          data={data}
          connections={myConns}
          canEdit={canEdit}
          saving={saving}
          onPick={(id) => save({ connectionId: id }, `This destination now uses ${(myConns.find((c) => c.id === id) || myConns.find((c) => c.primary) || {}).label || "that account"}`)}
        />
        <ActorRow
          actor={data.actor}
          name={name}
          canEdit={canEdit}
          saving={saving}
          onUseMine={() => save({ useMyAccount: true }, `${name} uploads and downloads for this project now use your account`)}
        />
        {provider === "acc" && !data.canUpload ? (
          <div className="integration-warning">
            This Autodesk account was connected before uploads were allowed. Connect it again to deliver files to ACC.
            <button className="bg-white text-bid border-bid shadow-none px-3 ml-2" onClick={connectAcc}>
              Reconnect
            </button>
          </div>
        ) : null}

        {provider === "procore" ? (
          // THE TWO PATHS UNDER THE CONNECTION (owner 2026-10-21): where models
          // and shop drawings go, readable at a glance, each with its picker.
          <div className="integration-section">
            <div className="integration-section-title">Procore connection</div>
            <div className="integration-row">
              <span>
                You are connected
                {procoreStatus && procoreStatus.email ? ` as ${procoreStatus.email}` : ""}
                {procoreStatus && PROCORE_METHOD[procoreStatus.method] ? ` ${PROCORE_METHOD[procoreStatus.method]}` : ""}
              </span>
            </div>
            {[
              { kind: "models", label: "Models" },
              { kind: "shopDrawings", label: "Shop drawings" },
            ].map(({ kind, label }) => {
              const t = (data.targets && data.targets[kind]) || {};
              return (
                <div key={kind} className="integration-row">
                  <b style={{ minWidth: 110 }}>{label}:</b>
                  <span className={t.set ? "integration-path" : "text-muted"}>{t.set ? t.path : "Not set"}</span>
                  {canEdit ? (
                    <button className="integration-link-btn" disabled={saving} onClick={() => setPicking(kind)}>
                      {t.set ? "Change" : "Choose folder"}
                    </button>
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : null}

        {provider === "procore" ? (
          <div className="integration-section">
            <div className="integration-section-title">Procore project</div>
            {data.linked ? (
              <div className="integration-row">
                <span className="integration-path">{data.linkPath}</span>
                {canEdit ? (
                  <>
                    <button className="integration-link-btn" onClick={() => setPicking("link")}>
                      Change
                    </button>
                    <button className="integration-link-btn danger" onClick={unlinkProcore}>
                      Unlink
                    </button>
                  </>
                ) : null}
              </div>
            ) : (
              <div className="integration-row">
                <span className="text-muted">Not linked</span>
                {canEdit ? (
                  <button className="integration-link-btn" onClick={() => setPicking("link")}>
                    Link a Procore project
                  </button>
                ) : null}
              </div>
            )}
          </div>
        ) : data.modelLink ? (
          <div className="integration-section">
            <div className="integration-section-title">Linked Revit model</div>
            <div className="integration-row">
              <span className="integration-path">{data.modelLink.path || data.modelLink.fileName}</span>
            </div>
          </div>
        ) : null}

        <div className="integration-section">
          <div className="integration-section-title">Delivery folders</div>
          {KIND_ROWS.map(({ kind, label, hint }) => {
            const t = (data.targets && data.targets[kind]) || {};
            return (
              <div key={kind} className="integration-kind">
                <div className="integration-kind-head">
                  <b>{label}</b>
                  {canEdit ? (
                    <span>
                      <button className="integration-link-btn" disabled={saving} onClick={() => setPicking(kind)}>
                        {t.set ? "Change" : "Choose folder"}
                      </button>
                      {t.set ? (
                        <button className="integration-link-btn danger" disabled={saving} onClick={() => save({ targets: { [kind]: null } })}>
                          Clear
                        </button>
                      ) : null}
                    </span>
                  ) : null}
                </div>
                <div className={t.set ? "integration-path" : "text-muted"}>{t.set ? t.path : "Not set"}</div>
                <small className="text-muted">{hint}</small>
                {kind === "shopDrawings" ? (
                  <label className="integration-toggle">
                    <Switch
                      size="small"
                      checked={!!(data.autoDeliver && data.autoDeliver.shopDrawings)}
                      disabled={!canEdit || saving || !t.set}
                      onChange={(on) => save({ autoDeliver: { shopDrawings: on } }, on ? `Approved shop drawings will be delivered to ${name}` : null)}
                    />
                    <span>Deliver approved shop drawings to {name}</span>
                  </label>
                ) : null}
                {kind === "models" ? (
                  <label className="integration-toggle">
                    <Switch
                      size="small"
                      checked={!!(data.autoDeliver && data.autoDeliver.models)}
                      disabled={!canEdit || saving || !(t.set || (data.targets && data.targets.navisworks && data.targets.navisworks.set))}
                      onChange={(on) => save({ autoDeliver: { models: on } }, on ? `Every new model version will be uploaded to ${name}` : null)}
                      data-testid="dest-auto-models"
                    />
                    <span>Upload every new model version here (uploaded on the Models page, or downloaded from another destination)</span>
                  </label>
                ) : null}
              </div>
            );
          })}
        </div>
        <small className="text-muted d-block mt-2">
          Folders are kept per project. Uploads (and downloads) use the account shown under &quot;Uploads and downloads run
          as&quot;, whoever saves a folder later. A new revision of the same drawing becomes a new version of the same file in {name}.
        </small>
      </div>
    );
  }

  return (
    <Modal
      open
      title={
        <span className="d-inline-flex align-items-center" style={{ gap: 8 }}>
          <Logo connected={!!(data && data.connected)} linked={provider === "procore" ? !!(data && data.linked) : false} size={22} />
          {name} settings - {projectTitle}
        </span>
      }
      onCancel={onClose}
      footer={null}
      width={620}
      destroyOnClose
    >
      {strip}
      {content}
    </Modal>
  );
}
